import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';

/**
 * Apple-style pickers that replace the browser's own (which look like Windows on a PC): a menu for choices, a
 * calendar for dates, hour and minute columns for times, and a colour picker. They float above everything, work
 * with the keyboard, and close when you click elsewhere or press Escape.
 */

type Variant = 'field' | 'inline' | 'bare';

/* ---------- floating layer ---------- */

function Floating({ anchor, open, onClose, children, minWidth, className, label }: {
  anchor: HTMLElement | null; open: boolean; onClose: () => void; children: ReactNode; minWidth?: number; className?: string; label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number; maxH: number; width: number } | null>(null);
  const place = useCallback(() => {
    if (!anchor || !ref.current) return;
    const r = anchor.getBoundingClientRect();
    const el = ref.current;
    const width = Math.max(minWidth ?? 0, r.width, el.scrollWidth);
    const h = el.scrollHeight;
    const vh = window.innerHeight, vw = window.innerWidth;
    const below = vh - r.bottom - 12, above = r.top - 12;
    const down = below >= Math.min(h, 260) || below >= above;
    const maxH = Math.max(120, Math.min(420, down ? below - 6 : above - 6));
    const left = Math.max(12, Math.min(vw - width - 12, r.right - width > r.left ? r.left : r.right - width));
    const top = down ? r.bottom + 6 : Math.max(12, r.top - 6 - Math.min(h, maxH));
    setPos({ left, top, maxH, width });
  }, [anchor, minWidth]);
  useLayoutEffect(() => { if (open) { setPos(null); requestAnimationFrame(place); } }, [open, place]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => { const t = e.target as Node; if (!ref.current?.contains(t) && !anchor?.contains(t)) onClose(); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); onClose(); anchor?.focus(); } };
    const onMove = (e: Event) => { if (!ref.current?.contains(e.target as Node)) place(); };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onMove, true);
    return () => { document.removeEventListener('pointerdown', onDown, true); document.removeEventListener('keydown', onKey, true); window.removeEventListener('resize', place); window.removeEventListener('scroll', onMove, true); };
  }, [open, onClose, anchor, place]);
  if (!open) return null;
  return createPortal(
    <div ref={ref} data-popover-child="" role="dialog" aria-label={label} className={`pk-float${className ? ' ' + className : ''}`}
      style={pos ? { left: pos.left, top: pos.top, maxHeight: pos.maxH, minWidth: pos.width } : { left: -9999, top: 0, visibility: 'hidden' }}>
      {children}
    </div>,
    document.body,
  );
}

/* ---------- Select ---------- */

export interface SelectOption<T extends string> { value: T; label: string; hint?: string; disabled?: boolean }

export function Select<T extends string>({ value, options, onChange, label, variant = 'field', disabled, className, style, placeholder, searchable }: {
  value: T; options: ReadonlyArray<SelectOption<T>>; onChange: (v: T) => void; label: string; variant?: Variant; disabled?: boolean;
  className?: string; style?: React.CSSProperties; placeholder?: string; searchable?: boolean;
}) {
  const btn = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [hi, setHi] = useState(0);
  const typed = useRef({ s: '', t: 0 });
  const searchRef = useRef<HTMLInputElement>(null);
  const search = searchable ?? options.length > 14;
  const shown = useMemo(() => (q.trim() ? options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase())) : options), [options, q]);
  const cur = options.find((o) => o.value === value);
  const close = useCallback(() => { setOpen(false); setQ(''); }, []);
  const choose = (o: SelectOption<T>) => { if (o.disabled) return; if (o.value !== value) onChange(o.value); close(); btn.current?.focus(); };

  useEffect(() => {
    if (!open) return;
    const i = Math.max(0, shown.findIndex((o) => o.value === value));
    setHi(i);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      list.current?.querySelector<HTMLElement>(`[data-i="${i}"]`)?.scrollIntoView({ block: 'center' });
      if (search) searchRef.current?.focus({ preventScroll: true });
    }));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { list.current?.querySelector<HTMLElement>(`[data-i="${hi}"]`)?.scrollIntoView({ block: 'nearest' }); }, [hi]);

  const onKey = (e: React.KeyboardEvent) => {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); setOpen(true); }
      return;
    }
    if (search && e.key.length === 1 && document.activeElement !== searchRef.current) { searchRef.current?.focus(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(shown.length - 1, h + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(0, h - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setHi(0); }
    else if (e.key === 'End') { e.preventDefault(); setHi(shown.length - 1); }
    else if (e.key === 'Enter' || (e.key === ' ' && !search)) { e.preventDefault(); const o = shown[hi]; if (o) choose(o); }
    else if (e.key === 'Tab') close();
    else if (!search && e.key.length === 1) {
      // Type-to-find, like a Mac menu.
      const now = Date.now();
      typed.current = { s: (now - typed.current.t < 700 ? typed.current.s : '') + e.key.toLowerCase(), t: now };
      const i = shown.findIndex((o) => o.label.toLowerCase().startsWith(typed.current.s));
      if (i >= 0) setHi(i);
    }
  };

  return (
    <>
      <button ref={btn} type="button" className={`pk-btn pk-${variant}${className ? ' ' + className : ''}`} style={style} disabled={disabled}
        aria-haspopup="listbox" aria-expanded={open} aria-label={label} onClick={() => setOpen((v) => !v)} onKeyDown={onKey}>
        <span className="pk-val">{cur?.label ?? placeholder ?? 'Choose…'}</span>
        <Icon name="chevronsUpDown" size={variant === 'inline' ? 15 : 16} className="pk-chev" />
      </button>
      <Floating anchor={btn.current} open={open} onClose={close} label={label} className="pk-menu">
        {search && (
          <div className="pk-search"><Icon name="search" size={15} />
            <input ref={searchRef} placeholder="Search" value={q} onChange={(e) => { setQ(e.target.value); setHi(0); }} onKeyDown={onKey} aria-label={`Search ${label.toLowerCase()}`} />
          </div>
        )}
        <div ref={list} className="pk-list" role="listbox" aria-label={label} tabIndex={-1} onKeyDown={onKey}>
          {shown.map((o, i) => (
            <div key={o.value} data-i={i} role="option" aria-selected={o.value === value} aria-disabled={o.disabled || undefined}
              className={`pk-opt${i === hi ? ' hi' : ''}${o.disabled ? ' dis' : ''}`} onPointerEnter={() => setHi(i)} onClick={() => choose(o)}>
              <span className="pk-tick">{o.value === value && <Icon name="check" size={15} stroke={2.6} />}</span>
              <span className="pk-lbl">{o.label}{o.hint && <small>{o.hint}</small>}</span>
            </div>
          ))}
          {!shown.length && <div className="pk-empty">No matches</div>}
        </div>
      </Floating>
    </>
  );
}

/* ---------- dates ---------- */

const pad = (n: number) => String(n).padStart(2, '0');
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const parse = (s: string) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s); return m ? { y: +m[1]!, m: +m[2]!, d: +m[3]! } : null; };
const fmt = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const todayStr = () => { const t = new Date(); return fmt(t.getFullYear(), t.getMonth() + 1, t.getDate()); };
export function niceDate(s: string) {
  const p = parse(s);
  if (!p) return '';
  return new Date(Date.UTC(p.y, p.m - 1, p.d)).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function Calendar({ value, onPick, min, max }: { value: string; onPick: (v: string) => void; min?: string; max?: string }) {
  const start = parse(value) ?? parse(todayStr())!;
  const [view, setView] = useState({ y: start.y, m: start.m });
  const first = new Date(Date.UTC(view.y, view.m - 1, 1));
  const lead = (first.getUTCDay() + 6) % 7; // weeks start on Monday
  const days = new Date(Date.UTC(view.y, view.m, 0)).getUTCDate();
  const cells: Array<number | null> = [...Array(lead).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  while (cells.length % 7) cells.push(null);
  const today = todayStr();
  const shift = (n: number) => setView((v) => { const m = v.m + n; return { y: v.y + Math.floor((m - 1) / 12), m: ((m - 1) % 12 + 12) % 12 + 1 }; });
  return (
    <div className="pk-cal">
      <div className="pk-cal-head">
        <b>{MONTHS[view.m - 1]} {view.y}</b>
        <span className="spacer" />
        <button type="button" className="pk-nav" aria-label="Previous month" onClick={() => shift(-1)}><Icon name="back" size={18} /></button>
        <button type="button" className="pk-nav" aria-label="Next month" onClick={() => shift(1)}><Icon name="chevron" size={18} /></button>
      </div>
      <div className="pk-cal-grid" role="grid">
        {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <span key={i} className="pk-dow">{d}</span>)}
        {cells.map((d, i) => {
          if (!d) return <span key={i} />;
          const s = fmt(view.y, view.m, d);
          const off = (!!min && s < min) || (!!max && s > max);
          return (
            <button key={i} type="button" disabled={off} className={`pk-day${s === value ? ' on' : ''}${s === today ? ' today' : ''}${i % 7 === 4 ? ' fri' : ''}`}
              aria-label={niceDate(s)} aria-pressed={s === value} onClick={() => onPick(s)}>{d}</button>
          );
        })}
      </div>
      <div className="pk-cal-foot"><button type="button" className="link" disabled={(!!min && today < min) || (!!max && today > max)} onClick={() => onPick(today)}>Today</button></div>
    </div>
  );
}

export function DateInput({ value, onChange, label, min, max, variant = 'field', disabled, allowClear }: {
  value: string; onChange: (v: string) => void; label: string; min?: string; max?: string; variant?: Variant; disabled?: boolean; allowClear?: boolean;
}) {
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button ref={btn} type="button" className={`pk-btn pk-${variant}`} disabled={disabled} aria-label={`${label}: ${niceDate(value) || 'not set'}`} aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className={`pk-val${value ? '' : ' ph'}`}>{niceDate(value) || 'Choose a date'}</span>
        <Icon name="calendar" size={variant === 'inline' ? 15 : 16} className="pk-chev" />
      </button>
      <Floating anchor={btn.current} open={open} onClose={() => setOpen(false)} label={label} className="pk-pop" minWidth={290}>
        <Calendar value={value} min={min} max={max} onPick={(v) => { onChange(v); setOpen(false); btn.current?.focus(); }} />
        {allowClear && value && <div className="pk-cal-foot" style={{ paddingTop: 0 }}><button type="button" className="link" onClick={() => { onChange(''); setOpen(false); }}>Clear</button></div>}
      </Floating>
    </>
  );
}

/* ---------- times ---------- */

const normTime = (s: string) => {
  const m = /^\s*(\d{1,2})[:.\s]?(\d{2})\s*$/.exec(s);
  if (!m) return null;
  const h = +m[1]!, mi = +m[2]!;
  return h < 24 && mi < 60 ? `${pad(h)}:${pad(mi)}` : null;
};

function TimeColumns({ value, onPick }: { value: string; onPick: (v: string, done: boolean) => void }) {
  const [h, m] = (normTime(value) ?? '12:00').split(':').map(Number) as [number, number];
  const hRef = useRef<HTMLDivElement>(null), mRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    for (const r of [hRef, mRef]) { const el = r.current?.querySelector<HTMLElement>('.on'); if (el && r.current) r.current.scrollTop = el.offsetTop - r.current.clientHeight / 2 + el.offsetHeight / 2; }
  }, []);
  return (
    <div className="pk-time">
      <div className="pk-col" ref={hRef} role="listbox" aria-label="Hour">
        {Array.from({ length: 24 }, (_, i) => <button key={i} type="button" role="option" aria-selected={i === h} className={i === h ? 'on' : ''} onClick={() => onPick(`${pad(i)}:${pad(m)}`, false)}>{pad(i)}</button>)}
      </div>
      <span className="pk-colon">:</span>
      <div className="pk-col" ref={mRef} role="listbox" aria-label="Minute">
        {Array.from({ length: 60 }, (_, i) => <button key={i} type="button" role="option" aria-selected={i === m} className={i === m ? 'on' : ''} onClick={() => onPick(`${pad(h)}:${pad(i)}`, true)}>{pad(i)}</button>)}
      </div>
    </div>
  );
}

/** A time you can type (e.g. 1330 or 13:30) or pick from hour and minute columns. */
export function TimeInput({ value, onChange, label, variant = 'field', disabled, allowClear }: {
  value: string; onChange: (v: string) => void; label: string; variant?: Variant; disabled?: boolean; allowClear?: boolean;
}) {
  const wrap = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const commit = () => {
    if (text == null) return;
    const t = normTime(text);
    if (t && t !== value) onChange(t);
    else if (!text.trim() && allowClear) onChange('');
    setText(null);
  };
  return (
    <span ref={wrap} className={`pk-btn pk-${variant} pk-timefield${disabled ? ' dis' : ''}`}>
      <input className="pk-val" inputMode="numeric" aria-label={label} placeholder="--:--" disabled={disabled} value={text ?? value}
        onFocus={(e) => e.currentTarget.select()} onChange={(e) => setText(e.target.value.replace(/[^\d:.]/g, '').slice(0, 5))} onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); (e.target as HTMLInputElement).blur(); } if (e.key === 'ArrowDown' && e.altKey) setOpen(true); }} />
      <button type="button" className="pk-clock" aria-label={`Choose ${label.toLowerCase()}`} disabled={disabled} onClick={() => setOpen((v) => !v)} aria-expanded={open}><Icon name="clock" size={variant === 'inline' ? 15 : 16} /></button>
      <Floating anchor={wrap.current} open={open} onClose={() => setOpen(false)} label={label} className="pk-pop" minWidth={190}>
        <TimeColumns value={value} onPick={(v, done) => { onChange(v); if (done) setOpen(false); }} />
        {allowClear && value && <div className="pk-cal-foot"><button type="button" className="link" onClick={() => { onChange(''); setOpen(false); }}>Clear</button></div>}
      </Floating>
    </span>
  );
}

/** Date and time together (stored as an ISO instant in the browser's zone). */
export function DateTimeInput({ value, onChange, label }: { value: string; onChange: (iso: string) => void; label: string }) {
  const d = value ? new Date(value) : null;
  const ok = d && Number.isFinite(d.getTime());
  const date = ok ? fmt(d!.getFullYear(), d!.getMonth() + 1, d!.getDate()) : '';
  const time = ok ? `${pad(d!.getHours())}:${pad(d!.getMinutes())}` : '';
  const emit = (ds: string, ts: string) => { const p = parse(ds); const t = normTime(ts || '12:00'); if (!p || !t) { onChange(''); return; } const [h, m] = t.split(':').map(Number); onChange(new Date(p.y, p.m - 1, p.d, h, m).toISOString()); };
  return (
    <div className="pk-dt">
      <DateInput label={`${label} date`} value={date} onChange={(v) => emit(v, time)} />
      <TimeInput label={`${label} time`} value={time} onChange={(v) => emit(date || todayStr(), v)} />
    </div>
  );
}

/* ---------- colour ---------- */

const hexToHsv = (hex: string) => {
  const n = parseInt(hex.slice(1), 16), r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  let h = 0;
  if (d) h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return { h: ((h * 60) + 360) % 360, s: mx ? d / mx : 0, v: mx };
};
const hsvToHex = (h: number, s: number, v: number) => {
  const f = (n: number) => { const k = (n + h / 60) % 6; return v - v * s * Math.max(0, Math.min(k, 4 - k, 1)); };
  return `#${[f(5), f(3), f(1)].map((x) => Math.round(x * 255).toString(16).padStart(2, '0')).join('').toUpperCase()}`;
};

function ColourPad({ value, onChange }: { value: string; onChange: (hex: string, done: boolean) => void }) {
  const [hsv, setHsv] = useState(() => hexToHsv(/^#[0-9a-f]{6}$/i.test(value) ? value : '#0A84FF'));
  const drag = (el: HTMLElement, e: React.PointerEvent, fn: (x: number, y: number) => void) => {
    el.setPointerCapture(e.pointerId);
    const at = (ev: { clientX: number; clientY: number }) => { const r = el.getBoundingClientRect(); fn(Math.max(0, Math.min(1, (ev.clientX - r.left) / r.width)), Math.max(0, Math.min(1, (ev.clientY - r.top) / r.height))); };
    at(e);
    const move = (ev: PointerEvent) => at(ev);
    const up = () => { el.removeEventListener('pointermove', move); el.removeEventListener('pointerup', up); };
    el.addEventListener('pointermove', move); el.addEventListener('pointerup', up);
  };
  const set = (n: typeof hsv) => { setHsv(n); onChange(hsvToHex(n.h, n.s, n.v), false); };
  return (
    <div className="pk-colour">
      <div className="pk-sv" style={{ background: `linear-gradient(to top,#000,transparent),linear-gradient(to right,#fff,${hsvToHex(hsv.h, 1, 1)})` }}
        onPointerDown={(e) => drag(e.currentTarget, e, (x, y) => set({ ...hsv, s: x, v: 1 - y }))} role="slider" aria-label="Colour shade" aria-valuenow={Math.round(hsv.s * 100)}>
        <i style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, background: hsvToHex(hsv.h, hsv.s, hsv.v) }} />
      </div>
      <div className="pk-hue" onPointerDown={(e) => drag(e.currentTarget, e, (x) => set({ ...hsv, h: x * 359.9 }))} role="slider" aria-label="Hue" aria-valuenow={Math.round(hsv.h)}>
        <i style={{ left: `${(hsv.h / 360) * 100}%`, background: hsvToHex(hsv.h, 1, 1) }} />
      </div>
    </div>
  );
}

/** A round colour button that opens a Mac-style colour picker. */
export function ColourButton({ value, onChange, label, className }: { value: string; onChange: (hex: string) => void; label: string; className?: string }) {
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button ref={btn} type="button" className={`pk-swatch${className ? ' ' + className : ''}`} aria-label={label} title={label} style={{ background: value }} onClick={() => setOpen((v) => !v)} aria-expanded={open} />
      <Floating anchor={btn.current} open={open} onClose={() => setOpen(false)} label={label} className="pk-pop" minWidth={240}>
        <ColourPad value={value} onChange={(hex) => onChange(hex)} />
      </Floating>
    </>
  );
}
